import { useMemo } from 'react';
import { buildApiUrl } from './api';
import { notifySessionExpired } from './sessionExpiry';
import { parseServerTiming } from './serverTiming';
import { notify as notifyApiTiming } from './apiTimingObserver';
import { parseStaleEdit, type StaleEdit } from './staleEdit';

/**
 * Read the `Server-Timing` header off a response and parse it, tolerating
 * every failure mode: header absent, `response.headers` missing entirely
 * (as in some hand-built test doubles), or a cross-origin response that
 * never exposed the header via CORS — all degrade to `null`, the same
 * outcome as a malformed header (AC-31). Never throws.
 */
function readServerTiming(response: Response) {
  try {
    return parseServerTiming(response.headers?.get?.('Server-Timing'));
  } catch {
    return null;
  }
}

/**
 * Emit a client-timing event without letting a misbehaving subscriber (or
 * anything else in the observer plumbing) break the API call it describes.
 */
function safeNotifyApiTiming(event: Parameters<typeof notifyApiTiming>[0]): void {
  try {
    notifyApiTiming(event);
  } catch {
    // Timing must never break the caller — see apiTimingObserver.ts.
  }
}

/**
 * Stall backstop for every request, in ms.
 *
 * `fetch` has no timeout of its own, so before this existed a request that
 * never got a response left its caller awaiting forever — the promise simply
 * never settled. The visible failure was a write dialog stuck on "Creating…"
 * with the row already written, no error and no way back but a page reload,
 * because the awaited POST never resolved and the `finally` that clears
 * `submitting` never ran.
 *
 * Deliberately generous: the backend logs anything over 1s as slow, so 60s is
 * far outside the range of a healthy request and this can only fire on a
 * genuine stall. It is a backstop, not a latency budget — pass an explicit
 * `timeoutMs` where a tighter one is meaningful.
 */
export const REQUEST_TIMEOUT_MS = 60_000;

/**
 * What a failed `makeApiCallJson` rejects with: the server's message, plus the
 * HTTP status and — where the endpoint publishes one — a machine-readable
 * `code`.
 */
export type ApiError = Error & { status?: number; code?: string; staleEdit?: StaleEdit };

/**
 * Shared API client utility for making authenticated API calls
 * This should be used throughout the application instead of duplicating makeApiCall logic
 */
export class ApiClient {
  private getAccessToken: () => string | null;

  constructor(getAccessToken: () => string | null) {
    this.getAccessToken = getAccessToken;
  }

  /**
   * Make an authenticated API call
   * @param url - The API endpoint URL (relative to base API URL)
   * @param options - Fetch options (method, body, headers, etc.)
   * @param timeoutMs - Stall backstop in ms (default {@link REQUEST_TIMEOUT_MS})
   * @returns Promise<Response>
   */
  async makeApiCall(
    url: string,
    options: RequestInit = {},
    timeoutMs: number = REQUEST_TIMEOUT_MS,
  ): Promise<Response> {
    const token = this.getAccessToken();

    // A caller may own cancellation (unmount, superseded request). Keep theirs
    // authoritative and layer the backstop on top: abort ours when theirs fires,
    // so their `signal.aborted` checks and AbortError handling still hold.
    const callerSignal = options.signal ?? null;
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onCallerAbort = () => controller.abort();
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener('abort', onCallerAbort);
    }

    try {
      return await fetch(buildApiUrl(url), {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        },
      });
    } catch (error) {
      // A timeout is reported as a distinct error rather than the AbortError
      // fetch raises. Callers that own cancellation suppress AbortError on
      // purpose (`if (error.name !== 'AbortError')`), so leaving it as one
      // would swallow the very failure this backstop exists to surface.
      if (timedOut && !callerSignal?.aborted) {
        const method = options.method ?? 'GET';
        const timeoutError = new Error(
          `Request timed out after ${timeoutMs}ms: ${method} ${url}`,
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    }
  }

  /**
   * Make an authenticated API call with custom timeout
   * @param url - The API endpoint URL (relative to base API URL)
   * @param options - Fetch options (method, body, headers, etc.)
   * @param timeoutMs - Timeout in milliseconds (default: 10000)
   * @returns Promise<Response>
   */
  async makeApiCallWithTimeout(url: string, options: RequestInit = {}, timeoutMs: number = 10000): Promise<Response> {
    return this.makeApiCall(url, options, timeoutMs);
  }

  /**
   * Make an authenticated API call and parse JSON response
   * @param url - The API endpoint URL (relative to base API URL)
   * @param options - Fetch options (method, body, headers, etc.)
   * @returns Promise<T>
   */
  async makeApiCallJson<T = any>(url: string, options: RequestInit = {}): Promise<T> {
    // Capture whether this request carried a bearer token *before* firing it, so
    // a 401 can be classified: a rejected authenticated request means the
    // session expired, whereas a 401 with no token (e.g. the login endpoint) is
    // a normal credential failure the caller should surface itself.
    // Invariant this relies on: public/optional-auth endpoints are always called
    // through a no-token client (getAccessToken → null), so `hadAuthToken` here
    // reliably means "an authenticated request", not merely "a token exists".
    const hadAuthToken = !!this.getAccessToken();
    const method = options.method || 'GET';
    // Client-observed duration per RPL-25: measured around the fetch round
    // trip (not the JSON parsing below), matched with whatever Server-Timing
    // the response carries. Read once, reused by whichever branch we take —
    // this must not change either branch's resolution/error, only observe it.
    const startedAt = performance.now();
    const response = await this.makeApiCall(url, options);
    const clientMs = performance.now() - startedAt;
    const server = readServerTiming(response);

    if (!response.ok) {
      // Expired/revoked token on an authenticated request → tear the session
      // down centrally (AuthContext logs out and drops to the login screen)
      // instead of every caller echoing a raw "Invalid token" string. Still
      // throws below so in-flight callers stop; their error surfaces are
      // unmounted by the redirect.
      if (response.status === 401 && hadAuthToken) {
        notifySessionExpired();
      }

      let errorMessage = `API call failed: ${response.status} ${response.statusText}`;
      let errorCode: string | undefined;
      let staleEdit: StaleEdit | undefined;

      try {
        const errorBody = await response.json();
        // Handle different error response formats
        errorMessage = errorBody.message || errorBody.error || errorMessage;
        // A machine-readable reason, where the endpoint publishes one. Prose
        // cannot be branched on, and a caller that needs to put one 400 on a
        // specific field and another in the form banner has no other signal:
        // the status is the same for both.
        if (typeof errorBody.code === 'string') errorCode = errorBody.code;
        staleEdit = parseStaleEdit(errorBody);
      } catch {
        // Fallback to text if JSON parsing fails
        try {
          const errorText = await response.text();
          if (errorText) errorMessage += ` - ${errorText}`;
        } catch {
          // If both JSON and text parsing fail, use the basic message
        }
      }

      // Attach the HTTP status so callers can branch precisely (e.g. 404 vs a
      // transient 5xx) without parsing the message. Backward-compatible — the
      // message is unchanged.
      const error = new Error(errorMessage) as ApiError;
      error.status = response.status;
      if (errorCode) error.code = errorCode;
      if (staleEdit) error.staleEdit = staleEdit;
      // A slow 500 is exactly the timing worth capturing — emit on the
      // failure path too, after the error is built so timing can never alter
      // its shape, and before the throw so no caller ordering changes.
      safeNotifyApiTiming({ url, method, status: response.status, clientMs, server });
      throw error;
    }

    safeNotifyApiTiming({ url, method, status: response.status, clientMs, server });

    // Handle 204 No Content or other empty responses gracefully
    try {
      return await response.json();
    } catch {
      return null as T;
    }
  }

  /**
   * GET request convenience method
   * @param url - The API endpoint URL (relative to base API URL)
   * @param options - Additional fetch options
   * @returns Promise<T>
   */
  async get<T = any>(url: string, options: RequestInit = {}): Promise<T> {
    return this.makeApiCallJson<T>(url, { ...options, method: 'GET' });
  }

  /**
   * POST request convenience method
   * @param url - The API endpoint URL (relative to base API URL)
   * @param data - Data to send in request body
   * @param options - Additional fetch options
   * @returns Promise<T>
   */
  async post<T = any>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.makeApiCallJson<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request convenience method
   * @param url - The API endpoint URL (relative to base API URL)
   * @param data - Data to send in request body
   * @param options - Additional fetch options
   * @returns Promise<T>
   */
  async put<T = any>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.makeApiCallJson<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request convenience method
   * @param url - The API endpoint URL (relative to base API URL)
   * @param data - Data to send in request body
   * @param options - Additional fetch options
   * @returns Promise<T>
   */
  async patch<T = any>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.makeApiCallJson<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request convenience method
   * @param url - The API endpoint URL (relative to base API URL)
   * @param options - Additional fetch options
   * @returns Promise<T>
   */
  async delete<T = any>(url: string, options: RequestInit = {}): Promise<T> {
    return this.makeApiCallJson<T>(url, { ...options, method: 'DELETE' });
  }
}

/**
 * Hook to get an API client instance
 * @param getAccessToken - Function to get the current access token
 * @returns ApiClient instance
 */
export const useApiClient = (getAccessToken: () => string | null): ApiClient => {
  return useMemo(() => new ApiClient(getAccessToken), [getAccessToken]);
};

/**
 * Standalone function for making API calls when you have the access token
 * @param getAccessToken - Function to get the current access token
 * @param url - The API endpoint URL (relative to base API URL)
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns Promise<Response>
 */
export const makeApiCall = async (
  getAccessToken: () => string | null,
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const client = new ApiClient(getAccessToken);
  return client.makeApiCall(url, options);
};
